// Global app state — the React replacement for Flutter's globals.dart.
// Holds the logged-in user, derived userId + colors, the user's locations and
// the currently-selected location (shared so the persistent header and every
// page agree), and login/logout actions.

import React, {
  createContext, useContext, useMemo, useState, useCallback, useEffect, useRef,
} from 'react';
import { loadUserData, saveUserData, clearSession } from '../utils/storage';
import { colorsFromUser } from '../theme/buildTheme';
import { disconnectMqtt } from '../mqtt/mqttClient';
import {
  getLocations, login, getLocationDetail, updateLocationSortOrder,
} from '../api/api';

const AppContext = createContext(null);

// A stored session can re-login (to refresh theme / IsSuperAdmin) only if it
// actually holds usable credentials. Guards against firing the login query with
// undefined / empty / the literal string "undefined" (which produced
// `MobileNumber = undefined and Password = 'undefined'`).
function hasValidCreds(u) {
  const mob = u && u.MobileNumber != null ? String(u.MobileNumber).trim() : '';
  const pass = u && u.Password != null ? String(u.Password).trim() : '';
  const bad = (v) => !v || v.toLowerCase() === 'undefined' || v.toLowerCase() === 'null';
  return /^\d{6,}$/.test(mob) && !bad(pass);
}

export function AppProvider({ children }) {
  const [userData, setUserData] = useState(() => loadUserData());

  const user = userData && userData.length ? userData[0] : null;
  const userId = user ? Number(user.UserId) : 0;
  const colors = useMemo(() => colorsFromUser(user), [user]);
  // Super Admin (project owner) — gates admin-only features. IsSuperAdmin comes
  // from the Users record on login.
  const isSuperAdmin = String(user?.IsSuperAdmin ?? '') === '1';
  const companyName = user?.CompanyName || '';

  // Refresh the cached session from the server once on load, so newly-added
  // fields (e.g. IsSuperAdmin) and any changed CompanyInfo/theme propagate
  // without a manual re-login. Uses the stored mobile + password.
  const refreshedRef = useRef(false);
  useEffect(() => {
    if (refreshedRef.current) return;
    const u = userData && userData.length ? userData[0] : null;
    // Best-effort only: skip silently if creds aren't usable. The app still runs
    // from the stored session + userId — startup does NOT depend on re-login.
    if (!hasValidCreds(u)) return;
    refreshedRef.current = true;
    login(u.MobileNumber, u.Password)
      .then((res) => {
        const arr = res.data;
        if (Array.isArray(arr) && arr[0] && arr[0].UserId) {
          saveUserData(arr);
          setUserData(arr);
        }
      })
      .catch(() => { /* keep the cached session on failure */ });
  }, [userData]);

  // Locations + the selected location — loaded once per session, shared app-wide
  // so the persistent header's location switcher works on every page.
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(false);
  // Distinguishes "load failed" (offer retry) from "account genuinely has none".
  const [locationsError, setLocationsError] = useState(false);
  const autoRetriedRef = useRef(false);

  // Common Devices strip visibility — controlled by the header button, hidden
  // by default so the room/device area gets the full space.
  const [showCommon, setShowCommon] = useState(false);

  // Reorder mode + who owns the selected location (Super Admin or owner may
  // reorder). Owner = the Location's MasterUserId.
  const [locationOwnerId, setLocationOwnerId] = useState(null);
  const [editOrder, setEditOrder] = useState(false);

  // Load (or reload) the user's locations. Exposed as `retryLocations` so the UI
  // can recover from a failed fetch without a full page reload.
  const loadLocations = useCallback(() => {
    if (!userId) { setLocations([]); setLocationId(null); setLocationsError(false); return; }
    setLocationsLoading(true);
    setLocationsError(false);
    getLocations(userId)
      .then((res) => {
        const locs = Array.isArray(res.data) ? res.data : [];
        setLocations(locs);
        setLocationId((cur) => (cur != null ? cur : (locs.length ? locs[0].LocationID : null)));
      })
      .catch(() => { setLocations([]); setLocationsError(true); })
      .finally(() => setLocationsLoading(false));
  }, [userId]);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  // Self-heal a transient load failure: retry once automatically a moment later.
  // Reset the guard whenever a load succeeds so a later failure can retry again.
  useEffect(() => {
    if (locationsError && !autoRetriedRef.current) {
      autoRetriedRef.current = true;
      const t = setTimeout(() => loadLocations(), 2500);
      return () => clearTimeout(t);
    }
    if (!locationsError) autoRetriedRef.current = false;
    return undefined;
  }, [locationsError, loadLocations]);

  const setSession = useCallback((arr) => {
    saveUserData(arr);
    setUserData(arr);
  }, []);

  // Re-fetch the logged-in user from the server and update the session (applies
  // any changed CompanyInfo/theme). Returns true on success.
  const refreshSession = useCallback(async () => {
    const u = userData && userData.length ? userData[0] : null;
    if (!hasValidCreds(u)) return false;
    try {
      const res = await login(u.MobileNumber, u.Password);
      const arr = res.data;
      if (Array.isArray(arr) && arr[0] && arr[0].UserId) {
        // eslint-disable-next-line no-console
        console.log('[Session] refreshed', { IsSuperAdmin: arr[0].IsSuperAdmin, ButtonColor: arr[0].ButtonColor, BackColor: arr[0].BackColor });
        saveUserData(arr);
        setUserData(arr);
        return true;
      }
    } catch (e) { /* keep current session on failure */ }
    return false;
  }, [userData]);

  const logout = useCallback(() => {
    disconnectMqtt();
    clearSession();
    setUserData(null);
    setLocations([]);
    setLocationId(null);
  }, []);

  // Resolve the selected location's owner (MasterUserId) for the reorder gate.
  // Leaving reorder mode whenever the location changes avoids a stale edit state.
  useEffect(() => {
    setEditOrder(false);
    if (locationId == null) { setLocationOwnerId(null); return undefined; }
    let alive = true;
    getLocationDetail(locationId)
      .then((res) => {
        if (!alive) return;
        const row = Array.isArray(res.data) ? res.data[0] : res.data;
        setLocationOwnerId(row ? Number(row.MasterUserId) : null);
      })
      .catch(() => { if (alive) setLocationOwnerId(null); });
    return () => { alive = false; };
  }, [locationId]);

  const isLocationOwner = !!userId && userId === locationOwnerId;
  const canReorder = isSuperAdmin || isLocationOwner;

  // Reorder the user's locations and persist their per-user SortOrder.
  const reorderLocations = useCallback(async (newList) => {
    setLocations(newList);
    await Promise.all(newList.map((l, i) =>
      updateLocationSortOrder(userId, l.LocationID, i).catch(() => null)));
  }, [userId]);

  const selectedLocation = useMemo(
    () => locations.find((l) => String(l.LocationID) === String(locationId)) || null,
    [locations, locationId]
  );

  const value = useMemo(
    () => ({
      userData,
      user,
      userId,
      colors,
      isAuthed: !!user,
      isSuperAdmin,
      companyName,
      setSession,
      refreshSession,
      logout,
      locations,
      locationId,
      setLocationId,
      selectedLocation,
      locationsLoading,
      locationsError,
      retryLocations: loadLocations,
      showCommon,
      setShowCommon,
      canReorder,
      editOrder,
      setEditOrder,
      reorderLocations,
    }),
    [userData, user, userId, colors, isSuperAdmin, companyName, setSession, refreshSession, logout, locations, locationId, selectedLocation, locationsLoading, locationsError, loadLocations, showCommon, canReorder, editOrder, reorderLocations]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
